import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import DesktopLayout from "@/components/Layout/DesktopLayout";
import { api, formatApiError, tokenStore } from "@/api/client";
import { useAuth } from "@/contexts/AuthContext";
import { Download, Edit2, History, Loader2, ArrowRightCircle, CheckCircle2, Copy, Link2, Link2Off, Search, ExternalLink } from "lucide-react";
import { toast } from "sonner";

export default function QuoteDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [q, setQ] = useState(null);
  const [revisions, setRevisions] = useState([]);
  const [err, setErr] = useState("");
  const [showConvert, setShowConvert] = useState(false);
  const [convForm, setConvForm] = useState({ quote_version_id: "", project_name: "", estimated_delivery_date: "", special_requirements: "" });
  const [converting, setConverting] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const [showLink, setShowLink] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/quote/quotes/${id}`);
      setQ(data);
      const { data: revs } = await api.get(`/quote/quotes/${id}/revisions`);
      setRevisions(revs || []);
    } catch (e) { setErr(formatApiError(e)); }
  };
  useEffect(() => { load(); }, [id]);

  const openConvert = () => {
    if (q?.converted_to_project_id) { navigate(`/projects/${q.converted_to_project_id}`); return; }
    setConvForm({
      quote_version_id: q.id,   // default to the currently-viewed version
      project_name: q?.subject || "",
      estimated_delivery_date: "",
      special_requirements: "",
    });
    setShowConvert(true);
  };
  const doConvert = async () => {
    if (!convForm.estimated_delivery_date) return toast.error("Delivery date required");
    const targetQuoteId = convForm.quote_version_id || q.id;
    setConverting(true);
    try {
      const { data } = await api.post(`/project/convert/${targetQuoteId}`, {
        project_name: convForm.project_name || undefined,
        estimated_delivery_date: convForm.estimated_delivery_date,
        special_requirements: convForm.special_requirements || undefined,
      });
      toast.success(`Project ${data.project_id} created`);
      setShowConvert(false);
      navigate(`/projects/${data.id}`);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d && d.code === "already_converted") {
        toast.info("Quote already converted — opening project");
        navigate(`/projects/${d.id}`);
      } else toast.error(formatApiError(e));
    } finally { setConverting(false); }
  };

  // When THIS quote's root has already been converted to a project but this
  // particular revision isn't the currently-active snapshot, offer a
  // one-click "push this revision to the linked project" action so Sales /
  // Sales Coord can refresh the project's quote_snapshot + print pack.
  const linkedRev = revisions.find((r) => r.converted_to_project_id);
  const canPushRevision = ["super_admin", "sales", "sales_coordination", "dealer"].includes(user?.role)
                          && linkedRev && !q.converted_to_project_id;
  const pushRevisionToProject = async () => {
    if (!linkedRev) return;
    if (!window.confirm(`Push this revision (v${q.version || 1}) to project ${linkedRev.converted_to_project_pid || ""}? The project's quote snapshot and Print Pack will be refreshed.`)) return;
    setPushing(true);
    try {
      const { data } = await api.post(`/project/sync-from-quote/${q.id}`);
      toast.success(`Project ${data.project_id} refreshed with this revision`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setPushing(false); }
  };

  if (err) return <DesktopLayout><div className="text-red-600">{err}</div></DesktopLayout>;
  if (!q) return <DesktopLayout><div className="text-slate-500">Loading…</div></DesktopLayout>;
  const t = q.totals || {};
  const pdfUrl = `${api.defaults.baseURL}/quote/quotes/${q.id}/pdf?auth=${encodeURIComponent(tokenStore.get() || "")}`;
  const version = q.version || 1;
  const canConvert = ["super_admin", "sales", "sales_coordination", "dealer"].includes(user?.role);
  const canEditLinks = ["super_admin", "sales", "sales_coordination", "production_coordination", "dealer"].includes(user?.role);

  const duplicate = async () => {
    if (!window.confirm(`Duplicate quote ${q.quote_number}?\n\nA new quote is created with the same items — you can edit it right away.`)) return;
    setDuplicating(true);
    try {
      const { data } = await api.post(`/quote/quotes/${q.id}/duplicate`);
      toast.success(`Created ${data.quote_number}`);
      navigate(`/quotes/${data.id}/edit`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setDuplicating(false); }
  };

  const unlinkEnquiry = async () => {
    if (!window.confirm("Unlink this enquiry from the quote?")) return;
    try {
      await api.post(`/quote/quotes/${q.id}/link-enquiry`, { enquiry_id: null });
      toast.success("Enquiry unlinked");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <DesktopLayout>
      <Link to="/quotes" className="hk-tiny-label text-orange-600 hover:text-orange-700">← All quotes</Link>
      <div className="flex items-end justify-between mt-2 mb-6 gap-3 flex-wrap">
        <div>
          <div className="font-mono text-slate-500 flex items-center gap-2">
            {q.quote_number}
            <span className="hk-badge hk-badge-progress" data-testid="quote-version">v{version}</span>
          </div>
          <h1 className="text-4xl font-black tracking-tighter" style={{ fontFamily: "Chivo" }}>{q.subject || "Quote"}</h1>
          <p className="text-slate-500 mt-1 text-sm">For <span className="font-bold">{q.customer_name}</span> · created {new Date(q.created_at).toLocaleString()} by {q.created_by_name}</p>
          <div className="mt-2 flex items-center gap-2 flex-wrap text-sm">
            {q.enquiry_ref ? (
              <>
                <Link to={`/enquiries/${q.enquiry_ref.id}`} data-testid="quote-open-enquiry"
                  className="inline-flex items-center gap-1.5 border border-orange-300 bg-orange-50 text-orange-700 hover:bg-orange-100 font-bold px-3 py-1.5 rounded-sm">
                  <Link2 className="w-3.5 h-3.5" />
                  Enquiry {q.enquiry_ref.enquiry_id || ""} · {q.enquiry_ref.name || ""}
                  <ExternalLink className="w-3 h-3" />
                </Link>
                {canEditLinks && (
                  <>
                    <button type="button" onClick={() => setShowLink(true)} data-testid="quote-change-enquiry"
                      className="text-xs text-slate-500 hover:text-slate-900 underline underline-offset-2">Change</button>
                    <button type="button" onClick={unlinkEnquiry} data-testid="quote-unlink-enquiry"
                      className="text-xs text-slate-500 hover:text-red-600 underline underline-offset-2 inline-flex items-center gap-1">
                      <Link2Off className="w-3 h-3" /> Unlink
                    </button>
                  </>
                )}
              </>
            ) : canEditLinks ? (
              <button type="button" onClick={() => setShowLink(true)} data-testid="quote-link-enquiry"
                className="inline-flex items-center gap-1.5 border border-slate-300 bg-white text-slate-700 hover:border-orange-500 hover:text-orange-700 font-bold px-3 py-1.5 rounded-sm">
                <Link2 className="w-3.5 h-3.5" /> Link an enquiry
              </button>
            ) : null}
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Link to={`/quotes/${q.id}/edit`} className="inline-flex items-center gap-2 bg-slate-800 hover:bg-slate-900 text-white font-bold px-4 py-2.5 rounded-sm" data-testid="quote-edit">
            <Edit2 className="w-4 h-4" /> Edit
          </Link>
          {canEditLinks && (
            <button type="button" onClick={duplicate} disabled={duplicating}
              className="inline-flex items-center gap-2 bg-white border border-slate-300 hover:border-orange-500 hover:text-orange-700 text-slate-700 font-bold px-4 py-2.5 rounded-sm disabled:opacity-50"
              data-testid="quote-duplicate">
              {duplicating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Copy className="w-4 h-4" />} Duplicate
            </button>
          )}
          {canConvert && (
            <button type="button" onClick={openConvert} disabled={converting}
              className={`inline-flex items-center gap-2 text-white font-bold px-4 py-2.5 rounded-sm disabled:opacity-50 ${q.converted_to_project_id ? "bg-indigo-600 hover:bg-indigo-700" : "bg-rose-600 hover:bg-rose-700"}`}
              data-testid="quote-convert">
              {converting ? <Loader2 className="w-4 h-4 animate-spin" /> : q.converted_to_project_id ? <CheckCircle2 className="w-4 h-4" /> : <ArrowRightCircle className="w-4 h-4" />}
              {q.converted_to_project_id ? "Open linked project" : "Convert to Project"}
            </button>
          )}
          {canPushRevision && (
            <button type="button" onClick={pushRevisionToProject} disabled={pushing}
              className="inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white font-bold px-4 py-2.5 rounded-sm disabled:opacity-50"
              data-testid="quote-push-revision">
              {pushing ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRightCircle className="w-4 h-4" />}
              Push this revision to project
            </button>
          )}
          <a href={pdfUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bold px-4 py-2.5 rounded-sm" data-testid="quote-pdf">
            <Download className="w-4 h-4" /> Download PDF
          </a>
        </div>
      </div>

      {revisions.length > 1 && (
        <div className="hk-card p-4 mb-6" data-testid="revisions-panel">
          <div className="flex items-center gap-2 mb-3">
            <History className="w-4 h-4 text-slate-500" />
            <h3 className="font-bold text-sm" style={{ fontFamily: "Chivo" }}>Version history</h3>
            <span className="hk-tiny-label">{revisions.length} version{revisions.length === 1 ? "" : "s"}</span>
          </div>
          <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {revisions
              .slice()
              .sort((a, b) => (b.version || 1) - (a.version || 1))
              .map((r) => {
                const isCurrent = r.id === q.id;
                return (
                  <li key={r.id}>
                    <Link
                      to={`/quotes/${r.id}`}
                      data-testid={`revision-link-${r.version || 1}`}
                      className={`flex items-center justify-between border rounded-sm px-3 py-2 text-sm hover:border-orange-500 ${isCurrent ? "border-orange-500 bg-orange-50" : "border-slate-200 bg-white"}`}
                    >
                      <div>
                        <div className="font-mono text-xs text-slate-500">{r.quote_number}</div>
                        <div className="font-bold">v{r.version || 1} {isCurrent && <span className="text-orange-600 text-xs">· current</span>}</div>
                      </div>
                      <div className="text-right text-xs">
                        <div className="font-bold">₹{((r.totals || {}).grand_total || 0).toLocaleString()}</div>
                        <div className="text-slate-500">{new Date(r.created_at).toLocaleDateString()}</div>
                      </div>
                    </Link>
                  </li>
                );
              })}
          </ul>
        </div>
      )}

      <div className="hk-card overflow-x-auto mb-6">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-900 text-white hk-tiny-label">
              <th className="text-left px-3 py-3">#</th>
              <th className="text-left px-3 py-3">Description</th>
              <th className="text-right px-3 py-3">W</th>
              <th className="text-right px-3 py-3">D</th>
              <th className="text-right px-3 py-3">H</th>
              <th className="text-right px-3 py-3">Qty</th>
              <th className="text-right px-3 py-3">Rate</th>
              <th className="text-right px-3 py-3">Amount</th>
            </tr>
          </thead>
          <tbody>
            {(q.lines || []).map((l) => (
              <React.Fragment key={l.line_no}>
                <tr className="border-t border-slate-100">
                  <td className="px-3 py-2 font-bold">{l.line_no}</td>
                  <td className="px-3 py-2">
                    <span className="font-bold">{l.cabinet_type}</span> of <span className="font-bold">{l.module_name}</span> in <span className="font-bold">{l.material_name}</span>
                    {l.door_type_name && <> with <span className="font-bold">{l.door_type_name}</span> Doors</>}
                    {l.handle_name && <> and <span className="font-bold">{l.handle_name}</span> Handle</>}
                  </td>
                  <td className="px-3 py-2 text-right font-mono">{l.width}</td>
                  <td className="px-3 py-2 text-right font-mono">{l.depth}</td>
                  <td className="px-3 py-2 text-right font-mono">{l.height}</td>
                  <td className="px-3 py-2 text-right">{l.quantity}</td>
                  <td className="px-3 py-2 text-right font-bold">₹{(l.unit_rate || 0).toLocaleString()}</td>
                  <td className="px-3 py-2 text-right font-bold">₹{(l.line_total || 0).toLocaleString()}</td>
                </tr>
                {(l.accessories || []).map((a, ai) => (
                  <tr key={`a-${ai}`} className="bg-slate-50/50 text-xs">
                    <td></td>
                    <td colSpan={5} className="px-3 py-1 italic text-slate-600">
                      <span className="hk-tiny-label mr-2">Acc.</span>{a.name} of <span className="font-bold">{a.brand}</span> · {a.model_number}
                    </td>
                    <td className="px-3 py-1 text-right">{a.qty} × ₹{(a.rate || 0).toLocaleString()}</td>
                    <td className="px-3 py-1 text-right font-bold">₹{(a.total || 0).toLocaleString()}</td>
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <div className="hk-card p-6 max-w-md ml-auto">
        <table className="w-full text-sm">
          <tbody>
            <SRow label="Modules" value={t.modules_total} />
            {Number(t.discount_amount) > 0 && (
              <SRow label={`Less: Discount on Modules (${t.discount_percent}%)`}
                value={-t.discount_amount} testid="qd-discount" />
            )}
            <SRow label="Accessories" value={t.accessories_total} />
            <SRow label="Transportation" value={t.transportation_cost} />
            <SRow label="Installation" value={t.installation_cost} />
            <SRow label="Subtotal" value={t.subtotal} bold />
            <SRow label={`Tax (${t.tax_percent}%)`} value={t.tax} />
            <tr className="border-t-2 border-slate-900">
              <td className="py-2 font-black">Grand total</td>
              <td className="text-right py-2 font-black text-xl">₹{(t.grand_total || 0).toLocaleString()}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {q.remarks && (
        <div className="hk-card p-4 mt-6">
          <div className="hk-tiny-label mb-1">Remarks</div>
          <div className="text-sm whitespace-pre-wrap">{q.remarks}</div>
        </div>
      )}
      {showConvert && (
        <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4" data-testid="convert-modal">
          <div className="bg-white rounded-sm shadow-2xl max-w-lg w-full p-6">
            <h2 className="text-2xl font-black tracking-tight mb-1" style={{ fontFamily: "Chivo" }}>Convert quote to Project</h2>
            <p className="text-sm text-slate-500 mb-4">The project starts at <b>Payment Verification</b>. The quote is snapshotted — later edits to the quote won&apos;t change this project.</p>
            <div className="space-y-3">
              {revisions && revisions.length > 1 && (
                <div>
                  <label className="hk-tiny-label block mb-1">Which version to convert *</label>
                  <select className="w-full border border-slate-300 rounded-sm p-2.5 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                    value={convForm.quote_version_id}
                    onChange={(e) => setConvForm({ ...convForm, quote_version_id: e.target.value })}
                    data-testid="convert-version-select">
                    {revisions.map((r) => (
                      <option key={r.id} value={r.id}>
                        v{r.version || 1} — {r.quote_number} · ₹{((r.totals || {}).grand_total || 0).toLocaleString("en-IN")}
                        {r.converted_to_project_id ? " · already converted" : ""}
                      </option>
                    ))}
                  </select>
                  <p className="text-[11px] text-slate-500 mt-1">
                    Default is the version you were viewing. Newer versions may exist for the same enquiry.
                  </p>
                </div>
              )}
              <div>
                <label className="hk-tiny-label block mb-1">Project name</label>
                <input type="text" className="w-full border border-slate-300 rounded-sm p-2.5 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                  value={convForm.project_name} onChange={(e) => setConvForm({ ...convForm, project_name: e.target.value })}
                  placeholder="e.g. Whitefield Penthouse Kitchen" data-testid="convert-name" />
                <p className="text-[11px] text-slate-500 mt-1">Project ID: <span className="font-mono font-bold">HK - {q.customer_name || "customer"} - {convForm.project_name || q.subject || "untitled"}</span></p>
              </div>
              <div>
                <label className="hk-tiny-label block mb-1">Estimated delivery date *</label>
                <input type="date" required className="w-full border border-slate-300 rounded-sm p-2.5 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                  value={convForm.estimated_delivery_date} onChange={(e) => setConvForm({ ...convForm, estimated_delivery_date: e.target.value })}
                  data-testid="convert-date" />
              </div>
              <div>
                <label className="hk-tiny-label block mb-1">Special requirements</label>
                <textarea rows={3} className="w-full border border-slate-300 rounded-sm p-2.5 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                  value={convForm.special_requirements} onChange={(e) => setConvForm({ ...convForm, special_requirements: e.target.value })}
                  placeholder="One per line" data-testid="convert-notes" />
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 mt-6">
              <button type="button" onClick={() => setShowConvert(false)} className="px-4 py-2 rounded-sm border border-slate-300 text-slate-700 hover:bg-slate-50 font-bold text-sm">Cancel</button>
              <button type="button" onClick={doConvert} disabled={converting || !convForm.estimated_delivery_date}
                className="px-5 py-2 rounded-sm bg-rose-600 hover:bg-rose-700 text-white font-bold inline-flex items-center gap-2 disabled:opacity-50" data-testid="convert-confirm">
                {converting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRightCircle className="w-4 h-4" />} Create project
              </button>
            </div>
          </div>
        </div>
      )}
      {showLink && (
        <LinkEnquiryModal
          quote={q}
          onClose={() => setShowLink(false)}
          onDone={async () => { setShowLink(false); await load(); }}
        />
      )}
    </DesktopLayout>
  );
}

function LinkEnquiryModal({ quote, onClose, onDone }) {
  const [rows, setRows] = useState(null);
  const [term, setTerm] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/enquiry"); setRows(data || []); }
      catch (e) { toast.error(formatApiError(e)); setRows([]); }
    })();
  }, []);

  const matches = (rows || []).filter((e) => {
    const t = term.trim().toLowerCase();
    if (!t) return true;
    return [e.enquiry_id, e.name, e.phone, e.location, e.assigned_sales_name]
      .some((v) => (v || "").toString().toLowerCase().includes(t));
  }).slice(0, 40);

  const pick = async (e) => {
    setSaving(true);
    try {
      const { data } = await api.post(`/quote/quotes/${quote.id}/link-enquiry`, { enquiry_id: e.id });
      if (data.warning) toast.warning(data.warning);
      toast.success(`Linked to enquiry ${e.enquiry_id || ""}`);
      await onDone();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-start justify-center p-4 overflow-y-auto" data-testid="link-enquiry-modal">
      <div className="bg-white rounded-sm shadow-2xl max-w-2xl w-full p-6 mt-12">
        <h2 className="text-2xl font-black tracking-tight mb-1" style={{ fontFamily: "Chivo" }}>Link an enquiry</h2>
        <p className="text-sm text-slate-500 mb-4">
          Pick the enquiry this quote belongs to. The enquiry gets a link back to <b>{quote.quote_number}</b>.
        </p>
        <div className="relative mb-3">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input type="text" autoFocus value={term} onChange={(ev) => setTerm(ev.target.value)}
            placeholder="Search by enquiry ID, name, phone or sales person"
            className="w-full border border-slate-300 rounded-sm pl-9 pr-3 py-2.5 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
            data-testid="link-enquiry-search" />
        </div>
        <div className="border border-slate-200 rounded-sm divide-y divide-slate-100 max-h-[50vh] overflow-y-auto">
          {rows === null && <div className="px-4 py-6 text-sm text-slate-500">Loading enquiries…</div>}
          {rows !== null && matches.length === 0 && (
            <div className="px-4 py-6 text-sm text-slate-500" data-testid="link-enquiry-empty">No enquiry matches that search.</div>
          )}
          {matches.map((e) => (
            <button key={e.id} type="button" disabled={saving} onClick={() => pick(e)}
              className="w-full text-left px-4 py-3 hover:bg-orange-50 disabled:opacity-50 flex items-center justify-between gap-3"
              data-testid={`link-enquiry-option-${e.enquiry_id || e.id}`}>
              <div>
                <div className="font-mono text-xs text-slate-500">{e.enquiry_id}</div>
                <div className="font-bold text-sm">{e.name || "—"}</div>
                <div className="text-xs text-slate-500">
                  {[e.phone, e.location, e.assigned_sales_name].filter(Boolean).join(" · ") || "—"}
                </div>
              </div>
              <div className="text-right text-xs">
                <span className="hk-badge hk-badge-progress">{(e.stage || "").replace(/_/g, " ")}</span>
                {e.linked_quote_number && (
                  <div className="text-[11px] text-amber-600 mt-1">already: {e.linked_quote_number}</div>
                )}
              </div>
            </button>
          ))}
        </div>
        <div className="flex items-center justify-end gap-2 mt-5">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-sm border border-slate-300 text-slate-700 hover:bg-slate-50 font-bold text-sm" data-testid="link-enquiry-cancel">Cancel</button>
        </div>
      </div>
    </div>
  );
}

function SRow({ label, value, bold, testid }) {
  const num = Number(value || 0);
  const isNeg = num < 0;
  const display = `${isNeg ? "-" : ""}₹${Math.abs(num).toLocaleString()}`;
  return (
    <tr className={bold ? "font-bold border-t border-slate-300" : ""} data-testid={testid}>
      <td className="py-1">{label}</td>
      <td className={`text-right py-1 ${isNeg ? "text-emerald-700" : ""}`}>{display}</td>
    </tr>
  );
}

